import { useEffect, useState } from 'react'
import { RefreshCw, ShieldAlert, Play, CheckCircle } from 'lucide-react'
import { useAuthStore } from '../services/authStore'
import useNotificationStore from '../services/notificationStore'
import PageShell from '../components/ui/PageShell'
import PageHeader from '../components/ui/PageHeader'
import DataTable from '../components/ui/DataTable'
import LoadingState from '../components/ui/LoadingState'
import ErrorState from '../components/ui/ErrorState'
import {
  applyCurrencyNormalizationBackfill,
  dryRunCurrencyNormalizationBackfill,
  getCurrencyNormalizationDiagnostics,
  listCurrencyNormalizationReceipts,
} from '../services/currencyNormalizationAdminService'

const SUMMARY_CARDS = [
  ['extractedReceipts', 'Extracted'],
  ['normalizedReceipts', 'Normalized'],
  ['ambiguousReceipts', 'Ambiguous'],
  ['missingCurrencyReceipts', 'Missing'],
  ['assumedCurrencyReceipts', 'Assumed'],
  ['invalidCurrencyReceipts', 'Invalid'],
  ['nonIsoCurrencyReceipts', 'Non-ISO legacy'],
  ['dashboardExcludedReceipts', 'Dashboard excluded'],
]

function SummaryCard({ label, value }) {
  return (
    <div className="rounded-lg border border-outline bg-surface p-4 shadow-sm">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className="mt-2 text-2xl font-semibold">{value}</div>
    </div>
  )
}

function BackfillSummary({ summary }) {
  if (!summary) return null
  const rows = [
    ['Scanned receipts', summary.scannedReceipts],
    ['Updated receipts', summary.updatedReceipts],
    ['Scanned products', summary.scannedProducts],
    ['Updated products', summary.updatedProducts],
    ['Normalized aliases', summary.normalizedAliases],
    ['Ambiguous', summary.ambiguousCurrencies],
    ['Missing', summary.missingCurrencies],
    ['Invalid', summary.invalidCurrencies],
    ['Unchanged', summary.unchanged],
    ['Errors', summary.errors],
  ]
  return (
    <dl className="grid grid-cols-2 gap-3 text-sm md:grid-cols-5">
      {rows.map(([label, value]) => (
        <div key={label} className="rounded border border-outline bg-surface-elevated p-3">
          <dt className="text-muted-foreground">{label}</dt>
          <dd className="mt-1 font-semibold">{value}</dd>
        </div>
      ))}
    </dl>
  )
}

export default function CurrencyNormalizationAdmin() {
  const { user } = useAuthStore()
  const showToast = useNotificationStore((state) => state.showToast)
  const [diagnostics, setDiagnostics] = useState(null)
  const [receipts, setReceipts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [backfillSummary, setBackfillSummary] = useState(null)
  const [running, setRunning] = useState('')

  async function load() {
    setLoading(true)
    setError('')
    try {
      const [nextDiagnostics, nextReceipts] = await Promise.all([
        getCurrencyNormalizationDiagnostics(),
        listCurrencyNormalizationReceipts({ limit: 50 }),
      ])
      setDiagnostics(nextDiagnostics)
      setReceipts(nextReceipts)
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Unable to load currency normalization diagnostics')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (user?.is_admin) load()
  }, [user?.is_admin])

  async function runDryRun() {
    setRunning('dry-run')
    try {
      const summary = await dryRunCurrencyNormalizationBackfill({ limit: 50, includeProducts: true })
      setBackfillSummary(summary)
      showToast?.({ type: 'success', message: 'Dry run completed' })
      await load()
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Dry run failed')
      showToast?.({ type: 'error', message: 'Dry run failed' })
    } finally {
      setRunning('')
    }
  }

  async function runApply() {
    if (!window.confirm('Apply currency normalization backfill to matching receipts?')) return
    setRunning('apply')
    try {
      const summary = await applyCurrencyNormalizationBackfill({ limit: 50, includeProducts: true })
      setBackfillSummary(summary)
      showToast?.({ type: 'success', message: 'Backfill apply completed' })
      await load()
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Backfill apply failed')
      showToast?.({ type: 'error', message: 'Backfill apply failed' })
    } finally {
      setRunning('')
    }
  }

  if (!user?.is_admin) {
    return (
      <PageShell className="p-4">
        <div className="rounded-lg border border-outline bg-surface p-6 shadow-sm">
          <h3 className="text-lg font-semibold">Access Denied</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            You are not authorised to inspect currency normalization diagnostics.
          </p>
        </div>
      </PageShell>
    )
  }

  return (
    <PageShell className="px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        title="Currency Normalization"
        subtitle="Inspect canonical currency extraction, dashboard exclusions, and safe backfill outcomes."
      />

      <div className="mb-4 flex flex-wrap gap-2">
        <button type="button" className="inline-flex items-center gap-2 rounded border border-outline bg-surface px-3 py-2 text-sm" onClick={load}>
          <RefreshCw size={16} /> Refresh
        </button>
        <button type="button" className="inline-flex items-center gap-2 rounded bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground" disabled={running !== ''} onClick={runDryRun}>
          <Play size={16} /> {running === 'dry-run' ? 'Running...' : 'Dry run'}
        </button>
        <button type="button" className="inline-flex items-center gap-2 rounded bg-danger px-3 py-2 text-sm font-semibold text-white" disabled={running !== ''} onClick={runApply}>
          <ShieldAlert size={16} /> {running === 'apply' ? 'Applying...' : 'Apply with confirmation'}
        </button>
      </div>

      {loading ? <LoadingState text="Loading currency normalization diagnostics..." /> : null}
      {error ? <ErrorState text={error} /> : null}

      {diagnostics ? (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {SUMMARY_CARDS.map(([key, label]) => (
              <SummaryCard key={key} label={label} value={diagnostics.summary[key]} />
            ))}
          </div>

          <section>
            <h2 className="mb-3 text-lg font-semibold">Backfill result</h2>
            <p className="mb-3 text-sm text-muted-foreground">Dry run does not write. Apply requires confirmation and uses the same scoped limit.</p>
            <BackfillSummary summary={backfillSummary} />
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold">Top raw tokens</h2>
            <DataTable
              rows={diagnostics.topRawTokens}
              columns={[
                { key: 'token', label: 'Token' },
                { key: 'count', label: 'Count' },
                { key: 'status', label: 'Status' },
                { key: 'sampleCurrency', label: 'Sample currency' },
              ]}
              emptyText="No token diagnostics"
            />
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold">Receipt diagnostics</h2>
            <DataTable
              rows={receipts}
              columns={[
                { key: 'receiptId', label: 'Receipt ID', render: (row) => row.receiptId.slice(0, 8) },
                { key: 'source', label: 'Source' },
                { key: 'host', label: 'Host' },
                { key: 'totalOriginalAmountDecimal', label: 'Amount' },
                { key: 'totalOriginalCurrency', label: 'Currency' },
                { key: 'moneyExtractionStatus', label: 'Status' },
                { key: 'moneyExtractionSource', label: 'Extraction source' },
                {
                  key: 'dashboardIncluded',
                  label: 'Dashboard',
                  render: (row) => row.dashboardIncluded
                    ? <span className="inline-flex items-center gap-1 text-success"><CheckCircle size={14} /> Included</span>
                    : row.exclusionReason,
                },
              ]}
              emptyText="No receipt diagnostics"
            />
          </section>
        </div>
      ) : null}
    </PageShell>
  )
}
